import { env } from "./env";
import { authGeneration } from "./privateDataCache";

export function assertCurrentSession(generation: number): void {
  if (generation !== authGeneration()) {
    throw new ApiError(409, "The session changed while this request was running.");
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface AiUsageEventDetail {
  path: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cachedTokens: number;
  aiCalls: number;
}

function apiErrorMessageFromBody(body: unknown, fallback: string): string {
  if (typeof body === "string") {
    const trimmed = body.trim();
    return trimmed || fallback;
  }
  if (body && typeof body === "object") {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail.trim();
    }
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const message = (item as { msg?: unknown }).msg;
          if (typeof message !== "string" || !message.trim()) return null;
          const location = (item as { loc?: unknown }).loc;
          const field = Array.isArray(location)
            ? location.filter((part) => part !== "body").join(" → ")
            : "";
          return field ? `${field}: ${message.trim()}` : message.trim();
        })
        .filter((message): message is string => Boolean(message));
      if (messages.length) return messages.join(" · ");
    }
    if (detail && typeof detail === "object") {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) {
        return message.trim();
      }
    }
  }
  return fallback;
}

type FetchOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Override base URL for this request only. */
  baseUrl?: string;
  /** Set to true on endpoints that legitimately return 401 for
   *  reasons other than session expiry (e.g. the admin-token gate
   *  for /api/v1/admin/*). Suppresses the global
   *  `meridian:unauthenticated` event so a missing admin token
   *  doesn't kick the user back to /login. */
  suppressGlobalUnauth?: boolean;
};

function readIntHeader(res: Response, name: string): number {
  const raw = res.headers.get(name);
  if (!raw) return 0;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function emitAiUsageEvent(detail: AiUsageEventDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<AiUsageEventDetail>("meridian:ai-usage", { detail }));
}

function emitAiUsage(path: string, res: Response): void {
  const totalTokens = readIntHeader(res, "X-Meridian-AI-Total-Tokens");
  if (totalTokens <= 0) return;
  emitAiUsageEvent({
    path,
    inputTokens: readIntHeader(res, "X-Meridian-AI-Input-Tokens"),
    outputTokens: readIntHeader(res, "X-Meridian-AI-Output-Tokens"),
    totalTokens,
    cachedTokens: readIntHeader(res, "X-Meridian-AI-Cached-Tokens"),
    aiCalls: readIntHeader(res, "X-Meridian-AI-Calls"),
  });
}

/**
 * Lean fetch wrapper for the Meridian API.
 * - Prepends VITE_API_BASE_URL
 * - JSON-encodes bodies
 * - Throws `ApiError` on non-2xx so callers can `try/catch` or rely on React Query
 * - Sends cookies (credentials: "include") so session auth works
 */
export async function api<T = unknown>(path: string, opts: FetchOptions = {}): Promise<T> {
  const generation = authGeneration();
  const { body, baseUrl, headers, suppressGlobalUnauth, ...rest } = opts;
  const url = `${baseUrl ?? env.apiBaseUrl}${path}`;

  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      credentials: "include",
      headers: {
        Accept: "application/json",
        // CSRF header-by-convention: the API rejects mutating requests
        // that lack this (browsers can't attach custom headers cross-site
        // without a preflight, which the CORS allowlist blocks). Safe on
        // GETs too — the server only checks mutating methods.
        "X-Requested-With": "fetch",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    // Network-level failure (API down, CORS misconfig, DNS, offline).
    // The browser raises TypeError("Failed to fetch") with no useful
    // detail, which historically left us guessing whether the API is
    // crashed or the user's network is gone. Translate to a structured
    // ApiError (status 0 = no HTTP response) with a copy that's
    // hosting-agnostic — works whether the user is on Railway, the
    // local docker compose stack, or a future K8s deploy. The
    // underlying browser cause is included so a developer reading the
    // console gets the specific reason (DNS / CORS / offline / etc).
    const cause = err instanceof Error ? err.message : String(err);
    throw new ApiError(
      0,
      "Can't reach the Meridian API. Try again in a moment — if it " +
        "keeps failing, the backend may be down. (" +
        cause +
        ")",
      { network: true, url, cause },
    );
  }

  assertCurrentSession(generation);
  if (!res.ok) {
    let detail: unknown;
    try {
      const text = await res.text();
      try {
        detail = JSON.parse(text);
      } catch {
        detail = text;
      }
    } catch {
      detail = null;
    }
    assertCurrentSession(generation);
    if (res.status === 401 && !suppressGlobalUnauth) {
      // Session cookie missing / expired / tampered. Broadcast so the
      // AuthProvider can flip to anonymous and the RequireAuth guard
      // can bounce the user to /login. Callers can suppress this for
      // endpoints where 401 means something else (admin-token gate).
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("meridian:unauthenticated"));
      }
    }
    throw new ApiError(
      res.status,
      apiErrorMessageFromBody(detail, `API ${res.status} ${res.statusText}`),
      detail,
    );
  }

  emitAiUsage(path, res);

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const result = (await res.json()) as T;
    assertCurrentSession(generation);
    return result;
  }
  const result = await res.text();
  assertCurrentSession(generation);
  return result as unknown as T;
}

/** Multipart companion to `api`; the browser supplies the boundary header. */
export async function apiForm<T = unknown>(
  path: string,
  body: FormData,
  opts: Omit<RequestInit, "body"> = {},
): Promise<T> {
  const generation = authGeneration();
  const url = `${env.apiBaseUrl}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...opts,
      method: opts.method ?? "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "fetch",
        ...opts.headers,
      },
      body,
    });
  } catch (err) {
    const cause = err instanceof Error ? err.message : String(err);
    throw new ApiError(0, `Can't reach the Meridian API. (${cause})`, {
      network: true,
      url,
      cause,
    });
  }
  assertCurrentSession(generation);
  if (!res.ok) {
    let detail: unknown = null;
    try {
      const raw = await res.text();
      try {
        detail = JSON.parse(raw);
      } catch {
        detail = raw;
      }
    } catch {
      // Leave the response detail empty.
    }
    assertCurrentSession(generation);
    if (res.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("meridian:unauthenticated"));
    }
    throw new ApiError(
      res.status,
      apiErrorMessageFromBody(detail, `API ${res.status} ${res.statusText}`),
      detail,
    );
  }
  const result = (await res.json()) as T;
  assertCurrentSession(generation);
  return result;
}
